import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectCoverflow, Navigation } from 'swiper/modules';
import ServiceWorkItem from './ServiceWorkItem';
import { useLocale } from '../context/LocaleContext';
import { resolveServiceMediaUrl } from '../utils/serviceContent';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import './ServicePortfolio.css';
import './ServicePortfolioCarousel.css';

const ServicePortfolio = ({ works = [], gallery = [], title, serviceId }) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [showAll, setShowAll] = useState(false);
  const sectionTitleRef = useRef(null);
  const visibleWorks = works.filter((work) => work.isActive !== false);
  const galleryItems = gallery.length
    ? gallery
    : visibleWorks.filter((work) => work.image).map((work) => ({ id: work.id, image: work.image, alt: work.title }));
  const displayedWorks = showAll ? visibleWorks : visibleWorks.slice(0, 1);
  const projectType = Number(serviceId) === 2 ? 'logo/' : Number(serviceId) === 3 ? 'uiux/' : Number(serviceId) === 4 ? 'plugin/' : '';

  if (!visibleWorks.length && !galleryItems.length) return null;

  const showLess = () => {
    setShowAll(false);
    setTimeout(() => {
      if (!sectionTitleRef.current) return;
      const top = sectionTitleRef.current.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <div className="service-details-section" ref={sectionTitleRef}>
        <h3 className="service-details-section-title">{title || t('serviceUi.portfolio.title')}</h3>
      </div>

      {!!galleryItems.length && (
        <div className="service-portfolio-carousel-wrapper">
          <Swiper
            key={locale.direction}
            dir={locale.direction}
            effect="coverflow"
            grabCursor
            loop={galleryItems.length > 2}
            centeredSlides={false}
            slidesPerView={1.7}
            coverflowEffect={{ rotate: 10, stretch: 60, depth: 120, modifier: 2, slideShadows: false }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            navigation={false}
            modules={[EffectCoverflow, Autoplay, Navigation]}
            className="service-portfolio-carousel-swiper"
            breakpoints={{
              320: { slidesPerView: 1.2, spaceBetween: 10 },
              640: { slidesPerView: 1.3, spaceBetween: 20 },
              768: { slidesPerView: 1.5, spaceBetween: 20 },
              1024: { slidesPerView: 1.7, spaceBetween: 20 },
            }}
          >
            {galleryItems.map((item, index) => (
              <SwiperSlide key={item.id || `${item.image}-${index}`} className="service-portfolio-carousel-slide">
                <div className="service-portfolio-carousel-item">
                  <img
                    src={resolveServiceMediaUrl(item.image)}
                    alt={item.alt || t('serviceUi.portfolio.imageAlt', { number: index + 1 })}
                    className="service-portfolio-carousel-image"
                    loading="lazy"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {!!visibleWorks.length && (
        <div className="service-portfolio-list">
          {displayedWorks.map((work, index) => (
            <div
              key={work.id || `${work.title}-${index}`}
              className={showAll && index > 0 ? `service-work-item-animated service-work-item-delay-${index}` : ''}
              style={showAll && index > 0 ? { animationDelay: `${(index - 1) * 0.1}s` } : {}}
            >
              <ServiceWorkItem work={work} defaultDetailLink={`/projects/${projectType}${work.id}`} />
            </div>
          ))}
        </div>
      )}

      {visibleWorks.length > 1 && (
        <div className="service-portfolio-more">
          <button type="button" className={showAll ? 'service-portfolio-less-btn' : 'service-portfolio-more-btn'} onClick={showAll ? showLess : () => setShowAll(true)}>
            {showAll ? t('serviceUi.common.showLess') : t('serviceUi.common.showMore')}
          </button>
        </div>
      )}
    </>
  );
};

export default ServicePortfolio;
